import {
  type CurrencyCode,
  type Instant,
  type LocalDate,
  type Money,
  type Rate,
  type Ratio,
  formatLocalDate,
  fromApiMoney,
  fromApiRateFraction,
  parseInstant,
  parseLocalDate,
  ratio,
  requireCurrencyCode,
  toApiMoney,
  toApiRateFraction,
} from '@taler/domain'
import type { OperationQuery, Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'
import { projectQuery, type QueryProjection, queryFingerprint } from '../http/query.ts'

export type LoanRateType = NonNullable<Schemas['LoanResponse']['rateType']>
export type LoanType = NonNullable<Schemas['LoanResponse']['loanType']>

/** Fractions of the original principal and of the term; each is absent when the server has no figure for it. */
export interface LoanProgress {
  readonly principalRepaid: Ratio | undefined
  readonly termElapsed: Ratio | undefined
}

export interface Loan {
  readonly id: string
  readonly name: string
  readonly currency: string
  readonly originalPrincipal: Money
  readonly outstandingPrincipal: Money
  readonly accruedInterest: Money
  /**
   * Not an exchange rate - `Rate` carries and renders `numeric(20,10)` without a JS
   * float, which is what this needs; `from`/`to` are both the loan's own currency since
   * there is no conversion here to carry.
   */
  readonly annualInterestRate: Rate
  readonly drawdownDate: LocalDate
  readonly endDate: LocalDate | undefined
  readonly lender: string | undefined
  readonly rateType: LoanRateType | undefined
  readonly loanType: LoanType | undefined
  readonly monthlyPayment: Money | undefined
  readonly overdue: boolean
  readonly progress: LoanProgress
  readonly nextAccrualDate: LocalDate
  readonly accrualCount: number
  readonly principalAccountId: string
  readonly interestExpenseAccountId: string
  readonly interestPayableAccountId: string
  readonly overpaymentAccountId: string
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface LoanPortfolio {
  readonly displayCurrency: CurrencyCode
  readonly totalOutstanding: Money
  readonly monthlyPayments: Money
  readonly estimatedRemainingInterest: Money
}

export type LoanPortfolioQuery = OperationQuery<'loans_summary'>

const PORTFOLIO_PROJECTION: QueryProjection<LoanPortfolioQuery> = {
  displayCurrency: (value) => value,
}

export interface LoanPayment {
  readonly entryId: string
  readonly date: LocalDate
  readonly amount: Money
  readonly principalPortion: Money
  readonly interestPortion: Money
  readonly overpaymentPortion: Money
}

export interface LoanDrawdown {
  readonly entryId: string
  readonly date: LocalDate
  readonly amount: Money
  readonly destinationAccountId: string
}

export interface LoanAccrualProjection {
  readonly date: LocalDate
  readonly interestAmount: Money
}

export interface CreateLoanInput {
  readonly name: string
  readonly originalPrincipal: Money
  readonly annualInterestRate?: Rate | undefined
  readonly drawdownDate: LocalDate
  readonly endDate?: LocalDate | undefined
  readonly lender?: string | undefined
  readonly rateType?: LoanRateType | undefined
  readonly loanType?: LoanType | undefined
  readonly monthlyPayment?: Money | undefined
}

/** A PUT replaces the loan's terms: an undefined field clears that term. */
export interface UpdateLoanTermsInput {
  readonly endDate: LocalDate | undefined
  readonly lender: string | undefined
  readonly rateType: LoanRateType | undefined
  readonly loanType: LoanType | undefined
  readonly monthlyPayment: Money | undefined
}

export interface RecordLoanDrawdownInput {
  readonly amount: Money
  readonly destinationAccountId: string
}

export interface RecordLoanPaymentInput {
  readonly amount: Money
  readonly date: LocalDate
  readonly sourceAccountId: string
}

export type LoanAccrualProjectionsQuery = OperationQuery<'loans_accrualProjections'>

const PROJECTIONS_PROJECTION: QueryProjection<LoanAccrualProjectionsQuery> = {
  asOf: (value) => value,
  through: (value) => value,
}

export const loanKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'loans', 'list'),
  detail: (ledgerId: string, loanId: string): CacheKey => ledgerKey(ledgerId, 'loans', 'detail', loanId),
  summary: (ledgerId: string, query: LoanPortfolioQuery): CacheKey =>
    ledgerKey(ledgerId, 'loans', 'summary', queryFingerprint(projectQuery(query, PORTFOLIO_PROJECTION))),
  accrualProjections: (ledgerId: string, loanId: string, query: LoanAccrualProjectionsQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'loans',
      'accrual-projections',
      loanId,
      queryFingerprint(projectQuery(query, PROJECTIONS_PROJECTION)),
    ),
  /** Everything a write to a loan (creation, a payment, a drawdown) makes stale. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'loans')],
} as const

export interface LoansRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly Loan[]>
  get(ledgerId: string, loanId: string, signal?: AbortSignal): Promise<Loan>
  create(ledgerId: string, input: CreateLoanInput): Promise<Loan>
  updateTerms(ledgerId: string, loanId: string, input: UpdateLoanTermsInput): Promise<Loan>
  delete(ledgerId: string, loanId: string): Promise<void>
  summary(ledgerId: string, query: LoanPortfolioQuery, signal?: AbortSignal): Promise<LoanPortfolio>
  recordPayment(ledgerId: string, loanId: string, input: RecordLoanPaymentInput): Promise<LoanPayment>
  recordDrawdown(ledgerId: string, loanId: string, input: RecordLoanDrawdownInput): Promise<LoanDrawdown>
  accrualProjections(
    ledgerId: string,
    loanId: string,
    query: LoanAccrualProjectionsQuery,
    signal?: AbortSignal,
  ): Promise<readonly LoanAccrualProjection[]>
}

export function createLoansRepository(client: TalerClient): LoansRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/loans`
  const one = (ledgerId: string, loanId: string) => `${base(ledgerId)}/${encodeURIComponent(loanId)}`

  return {
    async list(ledgerId, signal) {
      const loans = await client.call<Schemas['LoanResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return loans.map(toLoan)
    },

    async get(ledgerId, loanId, signal) {
      return toLoan(
        await client.call<Schemas['LoanResponse']>({
          method: 'GET',
          path: one(ledgerId, loanId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      return toLoan(
        await client.call<Schemas['LoanResponse'], Schemas['CreateLoanRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body: {
            name: input.name,
            originalPrincipal: apiMoney(input.originalPrincipal),
            ...(input.annualInterestRate === undefined
              ? {}
              : { annualInterestRateFraction: toApiRateFraction(input.annualInterestRate).rate }),
            drawdownDate: formatLocalDate(input.drawdownDate),
            ...(input.endDate === undefined ? {} : { endDate: formatLocalDate(input.endDate) }),
            ...(input.lender === undefined ? {} : { lender: input.lender }),
            ...(input.rateType === undefined ? {} : { rateType: input.rateType }),
            ...(input.loanType === undefined ? {} : { loanType: input.loanType }),
            ...(input.monthlyPayment === undefined ? {} : { monthlyPayment: apiMoney(input.monthlyPayment) }),
          },
        }),
      )
    },

    async updateTerms(ledgerId, loanId, input) {
      return toLoan(
        await client.call<Schemas['LoanResponse'], Schemas['UpdateLoanTermsRequest']>({
          method: 'PUT',
          path: `${one(ledgerId, loanId)}/terms`,
          body: {
            ...(input.endDate === undefined ? {} : { endDate: formatLocalDate(input.endDate) }),
            ...(input.lender === undefined ? {} : { lender: input.lender }),
            ...(input.rateType === undefined ? {} : { rateType: input.rateType }),
            ...(input.loanType === undefined ? {} : { loanType: input.loanType }),
            ...(input.monthlyPayment === undefined ? {} : { monthlyPayment: apiMoney(input.monthlyPayment) }),
          },
        }),
      )
    },

    async delete(ledgerId, loanId) {
      await client.call<undefined>({ method: 'DELETE', path: one(ledgerId, loanId) })
    },

    async summary(ledgerId, query, signal) {
      return toLoanPortfolio(
        await client.call<Schemas['LoanSummaryResponse']>({
          method: 'GET',
          path: `${base(ledgerId)}/summary`,
          query: projectQuery(query, PORTFOLIO_PROJECTION),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async recordPayment(ledgerId, loanId, input) {
      return toLoanPayment(
        await client.call<Schemas['LoanPaymentResponse'], Schemas['RecordLoanPaymentRequest']>({
          method: 'POST',
          path: `${one(ledgerId, loanId)}/payments`,
          body: {
            amount: apiMoney(input.amount),
            date: formatLocalDate(input.date),
            sourceAccountId: input.sourceAccountId,
          },
        }),
      )
    },

    async recordDrawdown(ledgerId, loanId, input) {
      return toLoanDrawdown(
        await client.call<Schemas['LoanDrawdownResponse'], Schemas['RecordLoanDrawdownRequest']>({
          method: 'POST',
          path: `${one(ledgerId, loanId)}/drawdowns`,
          body: { amount: apiMoney(input.amount), destinationAccountId: input.destinationAccountId },
        }),
      )
    },

    async accrualProjections(ledgerId, loanId, query, signal) {
      const projections = await client.call<Schemas['LoanAccrualProjectionResponse'][]>({
        method: 'GET',
        path: `${one(ledgerId, loanId)}/accrual-projections`,
        query: projectQuery(query, PROJECTIONS_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return projections.map(toLoanAccrualProjection)
    },
  }
}

/**
 * `@taler/domain`'s `CurrencyCode` is the full ISO 4217 list; the contract's `ApiMoney`
 * enum is only what the server accepts today, so the wire type is narrower. The value is
 * always one of both at runtime - this is a type gap between the two generated lists, not
 * a validation the client can usefully perform twice.
 */
function apiMoney(value: Money): Schemas['ApiMoney'] {
  return toApiMoney(value) as Schemas['ApiMoney']
}

function toLoan(response: Schemas['LoanResponse']): Loan {
  const currency = requireCurrencyCode(response.currency)
  return {
    id: response.id,
    name: response.name,
    currency: response.currency,
    originalPrincipal: fromApiMoney(response.originalPrincipal),
    outstandingPrincipal: fromApiMoney(response.outstandingPrincipal),
    accruedInterest: fromApiMoney(response.accruedInterest),
    annualInterestRate: fromApiRateFraction({
      rate: response.annualInterestRateFraction,
      from: currency,
      to: currency,
    }),
    drawdownDate: parseLocalDate(response.drawdownDate),
    endDate: response.endDate === undefined ? undefined : parseLocalDate(response.endDate),
    lender: response.lender,
    rateType: response.rateType,
    loanType: response.loanType,
    monthlyPayment: response.monthlyPayment === undefined ? undefined : fromApiMoney(response.monthlyPayment),
    overdue: response.overdue,
    progress: {
      principalRepaid: optionalRatio(response.progress.principalRepaid),
      termElapsed: optionalRatio(response.progress.termElapsed),
    },
    nextAccrualDate: parseLocalDate(response.nextAccrualDate),
    accrualCount: response.accrualCount,
    principalAccountId: response.principalAccountId,
    interestExpenseAccountId: response.interestExpenseAccountId,
    interestPayableAccountId: response.interestPayableAccountId,
    overpaymentAccountId: response.overpaymentAccountId,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}

function optionalRatio(value: string | undefined): Ratio | undefined {
  return value === undefined ? undefined : ratio(value)
}

function toLoanPortfolio(response: Schemas['LoanSummaryResponse']): LoanPortfolio {
  return {
    displayCurrency: requireCurrencyCode(response.displayCurrency),
    totalOutstanding: fromApiMoney(response.totalOutstanding),
    monthlyPayments: fromApiMoney(response.monthlyPayments),
    estimatedRemainingInterest: fromApiMoney(response.estimatedRemainingInterest),
  }
}

function toLoanPayment(response: Schemas['LoanPaymentResponse']): LoanPayment {
  return {
    entryId: response.entryId,
    date: parseLocalDate(response.date),
    amount: fromApiMoney(response.amount),
    principalPortion: fromApiMoney(response.principalPortion),
    interestPortion: fromApiMoney(response.interestPortion),
    overpaymentPortion: fromApiMoney(response.overpaymentPortion),
  }
}

function toLoanDrawdown(response: Schemas['LoanDrawdownResponse']): LoanDrawdown {
  return {
    entryId: response.entryId,
    date: parseLocalDate(response.date),
    amount: fromApiMoney(response.amount),
    destinationAccountId: response.destinationAccountId,
  }
}

function toLoanAccrualProjection(response: Schemas['LoanAccrualProjectionResponse']): LoanAccrualProjection {
  return { date: parseLocalDate(response.date), interestAmount: fromApiMoney(response.interestAmount) }
}
